import React, { useState, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Fab,
    TablePagination,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from "@mui/icons-material";

// *** IMPORTANTE: Cambiar el nombre del componente de formulario ***
import ComponentTypeForm from "./ComponentTypeForm"; 
// *** IMPORTANTE: Cambiar el hook a usar ***
import { useComponentType } from "../../../hooks/maintenance/useComponentType";
// Asumo que useAuthContext es genérico y no necesita cambios
import { useAuthContext } from "../../../context/AuthProvider";

const ComponentTypesDashboard = () => {
    // Renombrar 'type' a 'componentType' para claridad y evitar confusión con 'type' de JS
    const [formState, setFormState] = useState({ open: false, componentType: null });
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, componentType: null });
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { user } = useAuthContext();
    // Renombrar de 'comprobanteTypes' a 'componentTypes'
    const { componentTypes, isLoading, error, createMutation, updateMutation, deleteMutation } = useComponentType();

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenForm = (componentType = null) => {
        setFormState({ open: true, componentType });
    };

    const handleCloseForm = () => {
        setFormState({ open: false, componentType: null });
    };

    // --- Manejo del Submit del Formulario ---
    const handleSubmit = useCallback((data) => {
        // En esta tabla TIPO_COMPONENTE, no tenemos CreByID/UpdByID.
        // Si más adelante decides añadir esos campos, puedes reintroducir esta lógica.
        if (!user) {
             showAlert("Debe iniciar sesión", "error");
             return;
        }

        const payload = { ...data }; // 'data' ya es un objeto plano gracias a react-hook-form

        if (formState.componentType) { // Si estamos editando un tipo de componente existente
            // No necesitamos 'updbyid' aquí porque la tabla TIPO_COMPONENTE no tiene ese campo.
            // Si lo agregas en la DB, descomenta la línea de abajo.
            payload.updbyid = user.EmpID; 
            updateMutation.mutate(
                { id: formState.componentType.tipcomid, data: payload },
                {
                    onSuccess: () => {
                        showAlert("Tipo de componente actualizado correctamente");
                        handleCloseForm();
                    },
                    onError: (mutationError) => { // Renombrado a mutationError para evitar confusión
                        console.error("Error al actualizar tipo de componente:", mutationError);
                        showAlert(mutationError.message || "Error al actualizar", "error");
                    },
                }
            );
        } else { // Si estamos creando un nuevo tipo de componente
            // No necesitamos 'crebyid' aquí porque la tabla TIPO_COMPONENTE no tiene ese campo.
            // Si lo agregas en la DB, descomenta la línea de abajo.
            payload.crebyid = user.EmpID;
            createMutation.mutate(payload, {
                onSuccess: () => {
                    showAlert("Tipo de componente creado correctamente");
                    handleCloseForm();
                },
                onError: (mutationError) => {
                    console.error("Error al crear tipo de componente:", mutationError);
                    showAlert(mutationError.message || "Error al crear", "error");
                },
            });
        }
    }, [formState.componentType, createMutation, updateMutation, user]); // 'user' aún está en las dependencias por si se necesita más tarde.

    // --- Manejo de Confirmación de Eliminación ---
    const handleConfirmDelete = () => {
        // Se pasa directamente el ID para la eliminación física
        deleteMutation.mutate(confirmDialog.componentType.tipcomid, {
            onSuccess: () => {
                showAlert("Tipo de componente eliminado correctamente");
                setConfirmDialog({ open: false, componentType: null });
            },
            onError: (mutationError) => {
                console.error("Error al eliminar tipo de componente:", mutationError);
                showAlert(mutationError.message || "Error al eliminar", "error");
            },
        });
    };

    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    // --- Filtrado de datos ---
    // Usar 'componentTypes' en lugar de 'comprobanteTypes'
    const filteredComponentTypes = componentTypes.filter((item) =>
        item.tipcomnom.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Tipos de Componente</Typography>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
            ) : error ? (
                <Typography color="error">Error al cargar: {error.message}</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
                            <TableRow>
                                {/* Columnas de la tabla TIPO_COMPONENTE */}
                                <TableCell><strong>ID</strong></TableCell> {/* Añadir ID si es relevante mostrarlo */}
                                <TableCell><strong>Nombre</strong></TableCell>
                                <TableCell><strong>Descripción</strong></TableCell>
                                <TableCell align="center"><strong>Acciones</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredComponentTypes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((type) => (
                                <TableRow key={type.tipcomid}>
                                    <TableCell>{type.tipcomid}</TableCell>
                                    <TableCell>{type.tipcomnom}</TableCell>
                                    <TableCell>{type.tipcomdes || "—"}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => handleOpenForm(type)}>
                                                <EditIcon color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" onClick={() => setConfirmDialog({ open: true, componentType: type })}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        count={filteredComponentTypes.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            )}

            {/* FAB para crear */}
            <Fab color="primary" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={() => handleOpenForm()}>
                <AddIcon />
            </Fab>

            {/* Modal formulario */}
            {formState.open && (
                <ComponentTypeForm // Usar el nuevo nombre del componente de formulario
                    open={formState.open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmit}
                    defaultValues={formState.componentType || {}} // Pasar el tipo de componente
                />
            )}

            {/* Diálogo de confirmación de eliminación */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, componentType: null })}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Desea eliminar el tipo de componente "{confirmDialog.componentType?.tipcomnom}"?
                        Esta acción es irreversible.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, componentType: null })}>Cancelar</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar para alertas */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity}>{alert.message}</Alert>
            </Snackbar>
        </Paper>
    );
};

export default ComponentTypesDashboard;